import { Injectable, NgZone } from "@angular/core";
import { io, Socket } from "socket.io-client";
import { Subject } from "rxjs";
import { environment } from "../../../environments/environment";
import { StartSessionResponse } from "@models/support-chat.model";
import { UserStartPayload } from "@models/supportSession.model";

@Injectable({
    providedIn: "root",
})
export class SupportSocketService {
    private sockets: Partial<Record<"user" | "specialist" | "admin", Socket>> = {};
    private readonly messageSubject = new Subject<any>();
    private readonly sessionSubject = new Subject<any>();

    constructor(private readonly ngZone: NgZone) {}

    public onMessage() {
        return this.messageSubject.asObservable();
    }

    public onSessionUpdate() {
        return this.sessionSubject.asObservable();
    }

    public async userJoinSession(sessionPublicId: string) {
        const socket = await this.ensureSocket("user");
        socket.emit("joinSession", { sessionPublicId });
    }

    public async specialistJoinSession(sessionPublicId: string) {
        const socket = await this.ensureSocket("specialist");
        socket.emit("joinSession", { sessionPublicId });
    }

    public async connectAdmin(sessionPublicId: string): Promise<void> {
        const socket = await this.ensureSocket("admin");
        socket.emit("admin:join_session", { sessionPublicId });
    }

    public async connectUser(payload: UserStartPayload): Promise<StartSessionResponse> {
        const socket = await this.ensureSocket("user");
        return new Promise<StartSessionResponse>((resolve, reject) => {
            socket.emit("user:start_session", payload, (response: any) => {
                if (response?.ok) {
                    resolve(response as StartSessionResponse);
                } else {
                    reject(new Error(response?.error || "Unable to start session"));
                }
            });
        });
    }

    public async sendUserMessage(sessionPublicId: string, body: string): Promise<void> {
        const socket = await this.ensureSocket("user");
        return new Promise<void>((resolve, reject) => {
            socket.emit("user:message", { sessionPublicId, body }, (response: { ok: boolean; error?: string }) => {
                if (response?.ok) {
                    resolve();
                } else {
                    reject(new Error(response?.error || "Unable to send message"));
                }
            });
        });
    }

    public async connectSpecialist(payload: { sessionPublicId?: string; adminId: number }): Promise<{ session: any }> {
        const socket = await this.ensureSocket("specialist");
        return new Promise((resolve, reject) => {
            socket.emit("specialist:ready", payload, (response: any) => {
                if (response?.ok) {
                    resolve(response);
                } else {
                    reject(new Error(response?.error || "Unable to connect as specialist"));
                }
            });
        });
    }

    public async sendSpecialistMessage(sessionPublicId: string, body: string): Promise<void> {
        const socket = await this.ensureSocket("specialist");
        return new Promise<void>((resolve, reject) => {
            socket.emit(
                "specialist:message",
                { sessionPublicId, body },
                (response: { ok: boolean; error?: string }) => {
                    if (response?.ok) {
                        resolve();
                    } else {
                        reject(new Error(response?.error || "Unable to send message"));
                    }
                },
            );
        });
    }

    public async closeSession(sessionPublicId: string, adminId: number): Promise<void> {
        const socket = await this.ensureSocket("specialist");
        return new Promise<void>((resolve, reject) => {
            socket.emit(
                "specialist:close_session",
                { sessionPublicId, adminId },
                (response: { ok: boolean; error?: string }) => {
                    if (response?.ok) {
                        resolve();
                    } else {
                        reject(new Error(response?.error || "Unable to close session"));
                    }
                },
            );
        });
    }

    public disconnect(role?: "user" | "specialist"): void {
        if (role) {
            this.sockets[role]?.disconnect();
            this.sockets[role] = undefined;
            return;
        }
        this.sockets.user?.disconnect();
        this.sockets.specialist?.disconnect();
        this.sockets = {};
    }

    private async ensureSocket(role: "user" | "specialist" | "admin"): Promise<Socket> {
        const existing = this.sockets[role];
        if (existing && existing.connected) {
            return existing;
        }

        const baseUrl = environment.apiUrlBasePath.replace(/\/api$/, "");
        const socket = io(`${baseUrl}/support`, {
            query: { role },
            transports: ["websocket"],
        });

        socket.on("session:message", (payload) => {
            this.ngZone.run(() => this.messageSubject.next(payload));
        });

        socket.on("session:update", (payload) => {
            this.ngZone.run(() => this.sessionSubject.next(payload));
        });

        this.sockets[role] = socket;
        return socket;
    }
}
